import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Image from "next/image";

const OuterWrapper = styled.div`
  background-color: ${(props) =>
    (props.backgroundColor && props.backgroundColor) || "white"};
  overflow: hidden;
  display: flex;
  justify-content: center;
  margin: ${(props) =>
    (props.outerWrapperMargin && props.outerWrapperMargin) || "0"};
  padding: ${(props) =>
    (props.outerWrapperPadding && props.outerWrapperPadding) || "0"};
`;

const ContentWrapper = styled.div`
  font-style: normal;
  max-width: 79.5625rem;

  ${({ theme }) => theme.mq.xs`
    display: grid;
    padding-left: 0.75rem;
    padding-right: 0.3125rem;
  `}

  ${({ theme }) => theme.mq.sm`
    padding-left: 5rem;
    padding-right: 5rem;
    position: relative;
  `}

  ${({ theme }) => theme.mq.md`
    padding-top: 5.625rem;
  `}

  ${({ theme }) => theme.mq.lg`
    display: flex;
    padding-left: 2rem;
    padding-right: 2rem;
  `}
`;

const ImageWrapper = styled.div`
  ${({ theme }) => theme.mq.xs`
    display: grid;
    justify-content: center;
    grid-row: 4 / 4;
    width: calc(100% + 5rem);
    position: relative;
    left: -11%;
    padding-top: 1.5rem;
  `}

  ${({ theme }) => theme.mq.sm`
    max-width: 100%;
    padding-left: 3rem;
    padding-right: 3rem;
    padding-top: 2.125rem;
    position: relative;
    left: -8%;
  `}

  ${({ theme }) => theme.mq.md`
    display: inline-block;
    padding-top: 0;
    align-self: center;
    flex: 1; /* additionally, equal width */
    left: -2%;
    padding-left: 1rem;
    padding-right: 1rem;
  `}
`;

const TextWrapper = styled.div`
  font-weight: 400;

  ${({ theme }) => theme.mq.xs`
    padding-left: 1rem;
    padding-right: 1rem;
  `}
  ${({ theme }) => theme.mq.sm`
    padding-bottom: 1rem;
  `}

  ${({ theme }) => theme.mq.lg`
    display: flex;
    flex: 1; /* additionally, equal width */
    flex-direction: column;
    justify-content: center;
    padding-bottom: 0;
    padding-right: 0; // turn of at nth child even

    :nth-child(odd) {
      padding-right: 3.125rem; // turn of at nth child even
    }

    :nth-of-type(even) {
      padding-left: 0rem; // turn of at nth child even
    }
  `}
`;

const PointNumber = styled.div`
  font-weight: 600;

  ${({ theme }) => theme.mq.xs`
    color: ${theme.themeColors.primary};
    font-size: 2rem;
    padding-top: 2.8125rem;
    padding-bottom: 0.875rem;
  `}

  ${({ theme }) => theme.mq.sm`
    font-size: 3.5rem;
  `}

  ${({ theme }) => theme.mq.md`
    padding-top: 4.6875rem;
    font-size: 4rem;
  `}

  ${({ theme }) => theme.mq.lg`
    padding-top: 0;
    padding-bottom: 0;
  `}
`;

const CalloutText = styled.div`
  font-weight: 600;
  font-size: 1.5rem;
  letter-spacing: -0.025rem;
  line-height: 1.125;
  padding-bottom: 1rem;

  ${({ theme }) => theme.mq.sm`
    font-size: 2rem;
    padding-bottom: .5rem;
    max-width: 78%;
  `}

  ${({ theme }) => theme.mq.md`
    letter-spacing: -0.066875rem;
    line-height: 1.1875;
    max-width: 88%;
  `}
`;

const CopyText = styled.div`
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  padding-bottom: 1.25rem;

  ${({ theme }) => theme.mq.xs`
    width: 100%;
    border-style: none;
  `}

  ${({ theme }) => theme.mq.sm`
    font-size: 1rem;
    width: 90%;
    padding-bottom: 2rem;
  `}

  ${({ theme }) => theme.mq.lg`
    font-size: 0.9375rem;
    width: 100%;
    padding-bottom: 1.25rem;
  `}
`;

const LeadText = styled.div`
  font-weight: 600;
  font-size: 1.125rem;
  padding-bottom: 1rem;

  ${({ theme }) => theme.mq.sm`
    font-size: 1.425rem;
  `}

  ${({ theme }) => theme.mq.lg`
    padding-bottom: 0;
  `}

  ${({ theme }) => theme.mq.lg`
    font-size: 1.5rem;
  `}
`;

const BulletPointsList = styled.ul`
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  margin: 0 0 0.5rem 0;

  ${({ theme }) => theme.mq.md`
    margin: 0 0 0.75rem 0;
  `}

  ${({ theme }) => theme.mq.lg`
    margin: 0;
  `}

  ::last-child {
    margin: 0;
  }
`;

const BulletPointsListItem = styled.li`
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  list-style: disc;
  margin: 0;

  &::marker {
    color: ${({ theme }) => theme.themeColors.primary};
    font-size: 1.5rem;
  }
`;

function PageMid({
  backgroundColor,
  outerWrapperMargin,
  outerWrapperPadding,
  altText,
  imageFirst,
  image,
  pointNumber,
  callOutText,
  copy,
  leadText,
  bulletPoints,
}) {
  let content;
  const height = 742;
  const width = 767;

  if (imageFirst) {
    content = (
      <ContentWrapper>
        <ImageWrapper>
          <Image
            alt={altText}
            height={height}
            priority
            src={image}
            width={width}
          />
        </ImageWrapper>
        <TextWrapper>
          <PointNumber>{pointNumber}</PointNumber>
          <CalloutText>{callOutText}</CalloutText>
          <CopyText>{copy}</CopyText>
          <LeadText>{leadText}</LeadText>
          <BulletPointsList>
            {bulletPoints.map((item) => (
              <BulletPointsListItem key={item.id}>
                {item.text}
              </BulletPointsListItem>
            ))}
          </BulletPointsList>
        </TextWrapper>
      </ContentWrapper>
    );
  } else {
    content = (
      <ContentWrapper>
        <TextWrapper>
          <PointNumber>{pointNumber}</PointNumber>
          <CalloutText>{callOutText}</CalloutText>
          <CopyText>{copy}</CopyText>
          <LeadText>{leadText}</LeadText>
          <BulletPointsList>
            {bulletPoints.map((item) => (
              <BulletPointsListItem key={item.id}>
                {item.text}
              </BulletPointsListItem>
            ))}
          </BulletPointsList>
        </TextWrapper>
        <ImageWrapper>
          <Image
            alt={altText}
            height={height}
            priority
            src={image}
            width={width}
          />
        </ImageWrapper>
      </ContentWrapper>
    );
  }
  return (
    <OuterWrapper
      backgroundColor={backgroundColor}
      outerWrapperMargin={outerWrapperMargin}
      outerWrapperPadding={outerWrapperPadding}
    >
      {content}
    </OuterWrapper>
  );
}

export default PageMid;

PageMid.propTypes = {
  backgroundColor: PropTypes.string,
  outerWrapperMargin: PropTypes.string,
  outerWrapperPadding: PropTypes.string,
  altText: PropTypes.string,
  imageFirst: PropTypes.bool,
  image: PropTypes.string,
  pointNumber: PropTypes.string,
  callOutText: PropTypes.string,
  copy: PropTypes.string,
  leadText: PropTypes.string,
  bulletPoints: PropTypes.array,
};

PageMid.defaultProps = {
  backgroundColor: "white",
  outerWrapperMargin: "0",
  outerWrapperPadding: "0",
  altText: "",
  pointNumber: "",
  callOutText: "",
  copy: "",
  imageFirst: true,
  image: "",
  leadText: "",
  bulletPoints: [],
};
